import { Client, getStateCallbacks, type Room } from "colyseus.js";
import {
  ClientMessage,
  INPUT_SEND_HZ,
  RECONNECT_GRACE_MS,
  ServerMessage,
  type Facing,
  type InputPayload,
  type RejectedPayload,
  type ConcertRoomState,
  type PlayerSchema,
  type JoinOptions,
  type SyncPayload,
  type ShowStateSchema,
  type DiyaSchema,
  type TributeSchema,
  type EmotePayload,
  type TributePayload,
  type TributeAcceptedPayload,
  type FlowerSchema,
  type SitPayload,
  type StationPayload,
  type VoiceRequestPayload,
  type VoiceResponsePayload,
  type VoiceSignalPayload,
  type VoiceReportPayload,
  type VoiceInvitePayload,
  type VoiceAnswerPayload,
  type VoiceClosedPayload,
  AVATAR_IDS,
} from "@zg/shared";
import { clientConfig } from "../config.js";

/**
 * A value this browser keeps, so a second character replaces the first.
 *
 * Only used to recognise a returning tab. It is random, never leaves this
 * origin, and survives a reload — which is the whole point, since a reload is
 * exactly when somebody abandons a character without closing its socket.
 *
 * Private browsing, cleared storage or a blocked accessor all give a fresh one
 * every time, which simply means the old behaviour: nothing is evicted.
 */
const DEVICE_KEY = "zg.deviceId";

function deviceId(): string {
  try {
    const existing = window.localStorage.getItem(DEVICE_KEY);
    if (existing) return existing;
    const made = crypto.randomUUID();
    window.localStorage.setItem(DEVICE_KEY, made);
    return made;
  } catch {
    return "";
  }
}

export type ConnectionStatus =
  "idle" | "connecting" | "connected" | "reconnecting" | "disconnected" | "room_full" | "error";

/** The shape colyseus.js hands back for each entry in state.players. */
export interface NetPlayer {
  id: string;
  name: string;
  avatarId: string;
  x: number;
  y: number;
  facing: Facing;
  emote: string;
  emoteSeq: number;
  pose: string;
  seatId: string;
  singingUntil: number;
}

export interface NetworkHandlers {
  onStatus?: (status: ConnectionStatus, detail?: string) => void;
  onPlayerAdd?: (player: NetPlayer, sessionId: string, isSelf: boolean) => void;
  onPlayerChange?: (player: NetPlayer, sessionId: string, isSelf: boolean) => void;
  onPlayerRemove?: (sessionId: string, isSelf: boolean) => void;
  onRejected?: (payload: RejectedPayload) => void;
  /** Fired before a replacement room is wired up, so views can be cleared. */
  onSessionReset?: () => void;
  /** Show clock + phase, from the server. */
  onShowSync?: (payload: SyncPayload) => void;
  /** Fired when the room's show state changes in the patch stream. */
  onShowState?: (show: ShowStateSchema) => void;
  onDiyaAdd?: (diya: DiyaSchema) => void;
  onDiyaRemove?: (diya: DiyaSchema) => void;
  /** Fired whenever the approved-tribute list changes, with the whole list. */
  onTributes?: (tributes: TributeSchema[]) => void;
  onGlobalDiyaCount?: (count: number) => void;
  onGlobalFlowerCount?: (count: number) => void;
  onFlowerAdd?: (flower: FlowerSchema) => void;
  onFlowerRemove?: (flower: FlowerSchema) => void;
  onRadioStation?: (stationId: string) => void;
  onSingingCount?: (count: number) => void;
  onTributeAccepted?: (payload: TributeAcceptedPayload) => void;
  /** Someone standing next to you would like to talk. */
  onVoiceInvite?: (payload: VoiceInvitePayload) => void;
  onVoiceAnswer?: (payload: VoiceAnswerPayload) => void;
  onVoiceSignal?: (payload: VoiceSignalPayload) => void;
  onVoiceClosed?: (payload: VoiceClosedPayload) => void;
}

const INPUT_INTERVAL_MS = 1000 / INPUT_SEND_HZ;
const RECONNECT_RETRY_MS = 1200;
/** After the resume window closes, keep trying a fresh join for this long. */
const REJOIN_WINDOW_MS = 90_000;
/** Probes fired back-to-back on join to converge the clock offset quickly. */
const CLOCK_BURST = 5;
const CLOCK_BURST_GAP_MS = 300;
/** How many offset samples to keep; the median of these is the answer. */
const CLOCK_SAMPLES = 7;

/**
 * Owns the Colyseus connection: join, state fan-out, throttled input and
 * reconnection inside the server's grace window.
 */
export class NetworkClient {
  private readonly client = new Client(clientConfig.serverUrl);
  private room?: Room<ConcertRoomState>;
  private handlers: NetworkHandlers = {};
  private status: ConnectionStatus = "idle";

  private lastSentAt = 0;
  private lastDx = 0;
  private lastDy = 0;
  private recovering = false;
  private intentionalLeave = false;
  private lastJoinOptions: JoinOptions = { name: "", avatarId: AVATAR_IDS[0] };

  private probeSentAt = 0;
  private clockOffsetMs = 0;
  private readonly offsetSamples: number[] = [];
  private lastRttMs = 0;

  get sessionId(): string | undefined {
    return this.room?.sessionId;
  }

  get connected(): boolean {
    return this.status === "connected";
  }

  setHandlers(handlers: NetworkHandlers): void {
    this.handlers = handlers;
  }

  async join(name: string, avatarId: string): Promise<void> {
    this.intentionalLeave = false;
    this.lastJoinOptions = { name, avatarId };
    this.setStatus("connecting");
    try {
      const room = await this.client.joinOrCreate<ConcertRoomState>(clientConfig.roomName, {
        name,
        avatarId,
        deviceId: deviceId(),
      });
      this.attach(room);
      this.setStatus("connected");
      this.startClockBurst();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // Colyseus reports a full matchmaker the same way it reports a locked room.
      const full = /full|locked/i.test(message);
      this.setStatus(full ? "room_full" : "error", message);
      throw err;
    }
  }

  leave(): void {
    this.intentionalLeave = true;
    void this.room?.leave(true);
    this.room = undefined;
    this.setStatus("idle");
  }

  /**
   * Queue a movement intent. Sent at most INPUT_SEND_HZ times a second, and
   * only when the direction actually changed — a standing player sends nothing.
   */
  sendInput(dx: number, dy: number): void {
    if (!this.room || this.status !== "connected") return;

    const now = performance.now();
    const changed = dx !== this.lastDx || dy !== this.lastDy;
    if (!changed && now - this.lastSentAt < INPUT_INTERVAL_MS * 4) return;
    if (now - this.lastSentAt < INPUT_INTERVAL_MS) return;

    this.lastSentAt = now;
    this.lastDx = dx;
    this.lastDy = dy;
    const payload: InputPayload = { dx, dy, t: Math.round(now) };
    this.room.send(ClientMessage.Input, payload);
  }

  /**
   * Best estimate of the server's wall clock. Everything about the show is
   * anchored to this, never to the device clock — a phone whose clock is ten
   * minutes out would otherwise seek the tribute to the wrong minute.
   */
  serverNow(): number {
    return Date.now() + this.clockOffsetMs;
  }

  get clockOffset(): number {
    return this.clockOffsetMs;
  }

  get roundTripMs(): number {
    return this.lastRttMs;
  }

  /** Ask the server to play a reaction. It decides whether to allow it. */
  sendEmote(emote: string): void {
    const payload: EmotePayload = { emote };
    this.room?.send(ClientMessage.Emote, payload);
  }

  /** Light a diya where the server thinks this player is standing. */
  lightDiya(): void {
    this.room?.send(ClientMessage.LightDiya, {});
  }

  /** Submit a tribute into the moderation queue. Never straight to the wall. */
  offerFlower(): void {
    this.room?.send(ClientMessage.OfferFlower);
  }

  sit(seatId: string): void {
    this.room?.send(ClientMessage.Sit, { seatId } satisfies SitPayload);
  }

  stand(): void {
    this.room?.send(ClientMessage.Stand);
  }

  pray(): void {
    this.room?.send(ClientMessage.Pray);
  }

  sing(): void {
    this.room?.send(ClientMessage.Sing);
  }

  setStation(stationId: string): void {
    this.room?.send(ClientMessage.SetStation, { stationId } satisfies StationPayload);
  }

  requestVoice(to: string): void {
    this.room?.send(ClientMessage.VoiceRequest, { to } satisfies VoiceRequestPayload);
  }

  respondVoice(to: string, accept: boolean): void {
    this.room?.send(ClientMessage.VoiceResponse, { to, accept } satisfies VoiceResponsePayload);
  }

  sendVoiceSignal(to: string, data: unknown): void {
    this.room?.send(ClientMessage.VoiceSignal, { to, data } satisfies VoiceSignalPayload);
  }

  endVoice(): void {
    this.room?.send(ClientMessage.VoiceEnd);
  }

  reportVoice(to: string, reason: string): void {
    this.room?.send(ClientMessage.VoiceReport, { to, reason } satisfies VoiceReportPayload);
  }

  submitTribute(text: string): void {
    const payload: TributePayload = { text };
    this.room?.send(ClientMessage.SubmitTribute, payload);
  }

  /** Fire one clock probe. The reply arrives as a Sync message. */
  requestSync(): void {
    if (!this.room || this.status !== "connected") return;
    this.probeSentAt = performance.now();
    this.room.send(ClientMessage.RequestSync, {});
  }

  private handleSync(payload: SyncPayload): void {
    // Only a reply to our own probe carries a usable round trip; the
    // unsolicited one the server sends on join does not.
    if (this.probeSentAt) {
      const rtt = performance.now() - this.probeSentAt;
      this.probeSentAt = 0;
      this.lastRttMs = rtt;
      // Assume the reply took half the round trip to reach us.
      const offset = payload.serverTime + rtt / 2 - Date.now();
      this.offsetSamples.push(offset);
      if (this.offsetSamples.length > CLOCK_SAMPLES) this.offsetSamples.shift();
      const sorted = [...this.offsetSamples].sort((a, b) => a - b);
      this.clockOffsetMs = sorted[Math.floor(sorted.length / 2)] ?? 0;
    }
    this.handlers.onShowSync?.(payload);
  }

  private startClockBurst(): void {
    for (let i = 0; i < CLOCK_BURST; i += 1) {
      window.setTimeout(() => this.requestSync(), i * CLOCK_BURST_GAP_MS);
    }
  }

  private attach(room: Room<ConcertRoomState>): void {
    // A replacement room means every view on screen belongs to a dead session.
    if (this.room && this.room !== room) this.handlers.onSessionReset?.();
    this.room = room;
    const $ = getStateCallbacks(room);

    $(room.state).players.onAdd((player: PlayerSchema, sessionId: string) => {
      const isSelf = sessionId === room.sessionId;
      this.handlers.onPlayerAdd?.(player, sessionId, isSelf);
      $(player).onChange(() => {
        this.handlers.onPlayerChange?.(player, sessionId, isSelf);
      });
    });

    $(room.state).players.onRemove((_player: PlayerSchema, sessionId: string) => {
      this.handlers.onPlayerRemove?.(sessionId, sessionId === room.sessionId);
    });

    room.onMessage(ServerMessage.Sync, (payload: SyncPayload) => {
      this.handleSync(payload);
    });

    $(room.state.showState).onChange(() => {
      this.handlers.onShowState?.(room.state.showState);
    });

    $(room.state).diyas.onAdd((diya: DiyaSchema) => {
      this.handlers.onDiyaAdd?.(diya);
    });

    $(room.state).diyas.onRemove((diya: DiyaSchema) => {
      this.handlers.onDiyaRemove?.(diya);
    });

    // The wall is small and changes rarely, so hand over the whole list
    // rather than making every caller reassemble it from add/remove.
    const emitTributes = () => {
      this.handlers.onTributes?.([...room.state.tributes]);
    };
    $(room.state).tributes.onAdd(emitTributes);
    $(room.state).tributes.onRemove(emitTributes);

    $(room.state).flowers.onAdd((flower: FlowerSchema) => {
      this.handlers.onFlowerAdd?.(flower);
    });

    $(room.state).flowers.onRemove((flower: FlowerSchema) => {
      this.handlers.onFlowerRemove?.(flower);
    });

    $(room.state).listen("globalDiyaCount", (count: number) => {
      this.handlers.onGlobalDiyaCount?.(count);
    });

    $(room.state).listen("globalFlowerCount", (count: number) => {
      this.handlers.onGlobalFlowerCount?.(count);
    });

    $(room.state).listen("radioStation", (id: string) => {
      this.handlers.onRadioStation?.(id);
    });

    $(room.state).listen("singingCount", (count: number) => {
      this.handlers.onSingingCount?.(count);
    });

    room.onMessage(ServerMessage.VoiceInvite, (payload: VoiceInvitePayload) => {
      this.handlers.onVoiceInvite?.(payload);
    });

    room.onMessage(ServerMessage.VoiceAnswer, (payload: VoiceAnswerPayload) => {
      this.handlers.onVoiceAnswer?.(payload);
    });

    room.onMessage(ServerMessage.VoiceSignal, (payload: VoiceSignalPayload) => {
      this.handlers.onVoiceSignal?.(payload);
    });

    room.onMessage(ServerMessage.VoiceClosed, (payload: VoiceClosedPayload) => {
      this.handlers.onVoiceClosed?.(payload);
    });

    room.onMessage(ServerMessage.TributeAccepted, (payload: TributeAcceptedPayload) => {
      this.handlers.onTributeAccepted?.(payload);
    });

    room.onMessage(ServerMessage.Rejected, (payload: RejectedPayload) => {
      this.handlers.onRejected?.(payload);
    });

    room.onError((code, message) => {
      console.warn("[net] room error", code, message);
      this.setStatus("error", message);
    });

    room.onLeave((code) => {
      if (this.intentionalLeave) return;
      // 1000 is a clean close; anything else is a drop worth retrying.
      if (code === 1000) {
        this.setStatus("disconnected");
        return;
      }
      this.beginReconnect();
    });
  }

  /**
   * Recovery runs in two stages. First we spend the server's grace window
   * trying to resume the same session, which keeps the player's position.
   * If that expires — the usual cause is the server itself restarting, so the
   * room and its token are gone — we fall back to joining fresh rather than
   * stranding the player on a dead page.
   */
  private beginReconnect(): void {
    if (this.recovering) return;
    this.recovering = true;
    this.setStatus("reconnecting");
    void this.recoverLoop();
  }

  private async recoverLoop(): Promise<void> {
    const token = this.room?.reconnectionToken;

    if (token) {
      const resumeDeadline = Date.now() + RECONNECT_GRACE_MS;
      while (Date.now() < resumeDeadline && !this.intentionalLeave) {
        try {
          this.attach(await this.client.reconnect<ConcertRoomState>(token));
          this.recovering = false;
          this.setStatus("connected");
          this.startClockBurst();
          return;
        } catch {
          await delay(RECONNECT_RETRY_MS);
        }
      }
    }

    const rejoinDeadline = Date.now() + REJOIN_WINDOW_MS;
    while (Date.now() < rejoinDeadline && !this.intentionalLeave) {
      try {
        this.attach(
          await this.client.joinOrCreate<ConcertRoomState>(
            clientConfig.roomName,
            this.lastJoinOptions,
          ),
        );
        this.recovering = false;
        this.setStatus("connected");
        this.startClockBurst();
        console.info("[net] recovered with a fresh session");
        return;
      } catch {
        await delay(RECONNECT_RETRY_MS);
      }
    }

    this.recovering = false;
    this.setStatus("disconnected");
  }

  private setStatus(status: ConnectionStatus, detail?: string): void {
    if (this.status === status) return;
    this.status = status;
    this.handlers.onStatus?.(status, detail);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}
